import React from 'react'
import Select, { components } from 'react-select'
import Fade from 'react-reveal/Fade'
import chroma from 'chroma-js'
import moment from 'moment'

import SearchForm from '../components/search-form'
import PropertyCard from './cards/property'
import BoughtPropertyCard from './cards/bought'

import propertiesQuery from '../hooks/use-properties'
import boughtQuery from '../hooks/use-bought'

const orderOptions = [
  { label: "Highest price", value: "high" },
  { label: "Lowest price", value: "low" },
  { label: "Newest", value: "new" },
  { label: "Oldest", value: "old" },
  { label: "Auction", value: "auction" },
  { label: "Private sale", value: "private" },
  { label: "Off market", value: "off-market" },
]

const colourStyles = {
  control: styles => ({ ...styles, backgroundColor: 'white' }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    const color = chroma('#fff');
    return {
      ...styles,
      backgroundColor: isDisabled
        ? null
        : isSelected
          ? data.color
          : isFocused
            ? color.alpha(0.1).css()
            : null,
      color: isDisabled
        ? '#ccc'
        : isSelected
          ? chroma.contrast(color, 'white') > 2
            ? 'white'
            : 'black'
          : data.color,
      cursor: isDisabled ? 'not-allowed' : 'default',

      ':active': {
        ...styles[':active'],
        backgroundColor: !isDisabled && (isSelected ? data.color : color.alpha(0.3).css()),
      },
    };
  },
  input: styles => ({ ...styles }),
  placeholder: styles => ({ ...styles }),
  singleValue: (styles, { data }) => ({ ...styles }),
};

const DropdownIndicator = (props) => {
  let style = {}
  if (props.selectProps.menuIsOpen) style.transform = 'rotate(180deg)'

  return (
    <components.DropdownIndicator {...props}>
      <svg style={style} width="6px" height="5px" viewBox="0 0 101 88" version="1.1" xmlns="http://www.w3.org/2000/svg">
        <g stroke="none" strokeWidth="1" fillRule="evenodd" fill="#3C3C3C">
          <polygon transform="translate(50.422003, 44.000000) rotate(-90.000000) translate(-50.422003, -44.000000) " points="93.844005 94 7 44 93.844005 -6"></polygon>
        </g>
      </svg>
    </components.DropdownIndicator>
  )
}

const PropertyBlocks = (props) => {

  const { type, pagePropertyBlocks, dark } = props

  const propertyData = propertiesQuery()
  const boughtData = boughtQuery()

  let initialProperties = propertyData.allWpProperty.nodes
  if (process.env.GATSBY_LOCATION === 'nz') { initialProperties = [...boughtData.allWpBought.nodes, ...propertyData.allWpProperty.nodes] }
  let searchProperties = initialProperties

  if (type !== 'lease') {
    initialProperties = initialProperties.filter((el, i) => {
      if (type === 'buy' && el.propertyData?.website_status?.toUpperCase() === 'SOLD') return false
      if (type === 'sold') return el.propertyData?.website_status?.toUpperCase() === 'SOLD'
      let isApartment = el.propertyMeta?.property_styles.includes('WHITEFOX Classic') || el.propertyMeta?.property_styles.includes('WHITEFOX Apartments')
      if (type === 'apartments') return isApartment
      return !isApartment
    })
    searchProperties = propertyData.allWpProperty.nodes.filter((el, i) => {
      if (type === 'buy' && el.propertyData.website_status.toUpperCase() === 'SOLD') return false
      if (type === 'sold') return el.propertyData.website_status.toUpperCase() === 'SOLD'
      return true
    })
  }

  // Sort by date
  initialProperties.sort((a, b) => a.date < b.date ? 1 : -1)
  searchProperties.sort((a, b) => a.date < b.date ? 1 : -1)

  // State
  const [list, setList] = React.useState(initialProperties)
  const [length, setLength] = React.useState(6)
  const [columns, setColumns] = React.useState('four-col')
  const [order, setOrder] = React.useState('new')

  // Refs
  const orderRef = React.useRef(null)
  const loadRef = React.useRef(null)

  React.useEffect(() => {

    let timeout = null
    const onScroll = () => {
      let position = loadRef?.current?.getBoundingClientRect()
      if (!position) return
      if (position.top >= 0 && position.bottom <= window.innerHeight) {
        if (!timeout) {
          timeout = setTimeout(() => {
            setLength(length + 6)
            timeout = null
          }, 10)
        }
      } else {
        clearTimeout(timeout)
      }
    }

    if (pagePropertyBlocks !== "WFLanding") {
      window.addEventListener('scroll', onScroll)
    }
    else {
      setLength(12)
    }

    return () => {
      window.removeEventListener('scroll', onScroll)
      clearTimeout(timeout)
    }

  }, [])

  React.useEffect(() => {
    _formCallback(list)
  }, [order])

  const _sortList = (list) => {
    switch (order) {
      case 'high':
        list.sort((a, b) => a.propertyData.price_to < b.propertyData.price_to ? 1 : -1)
        break
      case 'low':
        list.sort((a, b) => a.propertyData.price_to < b.propertyData.price_to ? -1 : 1)
        list
      case 'old':
        list.sort((a, b) => moment(a.date).isBefore(b.date) ? -1 : 1)
        break
      case 'auction':
        list.sort((a, b) => {
          if (!a.propertyData?.auction_date) {
            if (!b.propertyData?.auction_date) {
              let as = a.propertyData?.website_status
              let bs = b.propertyData?.website_status
              if (as === bs) return 0
              if (as === 'sold') return 1
              if (bs === 'sold') return -1
            }
            return -1
          }
          if (!b.propertyData?.auction_date) return 1

          return moment(a.propertyData?.auction_date).isAfter(b.propertyData?.auction_date) ? 1 : -1
        })
        break
      case 'private':
        list.sort((a, b) => {
          let al = a.propertyData?.listing_type
          let bl = b.propertyData?.listing_type
          if (al === 'PRIVATE SALE' && bl !== 'PRIVATE SALE') return -1
          if (bl === 'PRIVATE SALE' && al !== 'PRIVATE SALE') return 1
          let as = a.propertyData?.website_status
          let bs = b.propertyData?.website_status
          if (as === 'sold') return 1
          if (as === 'current') return -1
          if (bs === 'current') return 1
          if (bs === 'sold') return -1
          return 0
        })
        break
      case 'off-market':
        list.sort((a, b) => {
          let al = a.propertyData?.listing_type
          let bl = b.propertyData?.listing_type
          if (al === 'OFF MARKET' && bl !== 'OFF MARKET') return -1
          if (bl === 'OFF MARKET' && al !== 'OFF MARKET') return 1
          let as = a.propertyData?.website_status
          let bs = b.propertyData?.website_status
          if (as === 'sold') return 1
          if (as === 'current') return -1
          if (bs === 'current') return 1
          if (bs === 'sold') return -1
        })
        break
      default:
        //case 'new':
        // Sort by date
        list.sort((a, b) => {
          if ( type === 'sold' ) {
            if (!a.propertyData?.sale_date) {
              return 1
            }
            if (!b.propertyData?.sale_date) {
              return -1
            }
            return moment(a.propertyData?.sale_date).isBefore(b.propertyData?.sale_date) ? 1 : -1
          } else if (type === 'buy') {
            if (a.propertyData?.campaign_sort_date && b.propertyData?.campaign_sort_date) {
              return moment(a.propertyData?.campaign_sort_date).isBefore(b.propertyData?.campaign_sort_date) ? -1 : 1
            }
            if (!a.propertyData?.campaign_sort_date && !b.propertyData?.campaign_sort_date) {
              return moment(a.date).isBefore(b.date) ? 1 : -1
            }
            if (!a.propertyData?.campaign_sort_date && b.propertyData?.campaign_sort_date) return -1
            if (!b.propertyData?.campaign_sort_date && a.propertyData?.campaign_sort_date) return 1
            return 0
          } else {
            // ALL
            if (a.propertyData?.website_status !== 'sold' && b.propertyData?.website_status === 'sold') return -1
            if (a.propertyData?.website_status === 'sold' && b.propertyData?.website_status !== 'sold') return 1

            if (process.env.GATSBY_LOCATION === 'nz') {
              // NZ Project if .acf exists display first
              if (a.acf?.price && !b.acf?.price) return -1
            }

            // Buy
            if (a.propertyData?.website_status !== 'sold' && b.propertyData?.website_status !== 'sold') {
              if (a.propertyData?.campaign_sort_date && b.propertyData?.campaign_sort_date) {
                return moment(a.propertyData?.campaign_sort_date).isBefore(b.propertyData?.campaign_sort_date) ? -1 : 1
              }
              if (!a.propertyData?.campaign_sort_date && !b.propertyData?.campaign_sort_date) {
                return moment(a.date).isBefore(b.date) ? 1 : -1
              }
              if (!a.propertyData?.campaign_sort_date && b.propertyData?.campaign_sort_date) return -1
              if (!b.propertyData?.campaign_sort_date && a.propertyData?.campaign_sort_date) return 1
            } 

            // Sold
            if (!a.propertyData?.sale_date) {
              return 1
            }
            if (!b.propertyData?.sale_date) {
              return -1
            }
            return moment(a.propertyData?.sale_date).isBefore(b.propertyData?.sale_date) ? 1 : -1
          }
          
        })
        // Split sold & current up
        if (type === 'lease') {
          list.sort((a, b) => {
            let as = a.propertyData?.status.toUpperCase()
            let bs = b.propertyData?.status.toUpperCase()
            if (as === bs) return 0
            if (as !== 'CURRENT') return 1
            if (bs !== 'CURRENT') return -1
            return 0
          })
        } else {
          list.sort((a, b) => {
            let as = a.propertyData?.website_status
            let bs = b.propertyData?.website_status
            if (as === bs) return 0
            if (as === 'sold') return 1
            if (bs === 'sold') return -1
            return 0
          })
        }
        break
    }
    return [...list]
  }

  const _formCallback = (arr) => {
    setList(_sortList(arr))
  }

  const _orderCallback = (option) => {
    setLength(12)
    setOrder(option.value)
  }

  const _renderCard = (el, i) => {
    const { propertyData, propertyMeta, propertyImages, acf, slug } = el
    if (acf) {
      return (
        <BoughtPropertyCard
          key={i}
          url={process.env.GATSBY_LOCATION === 'nz' ? "/project/" + slug : "/bought/" + slug}
          imageSource={acf.featuredImage?.localFile?.publicURL}
          title={el.title}
          specs={acf.specs}
          price={acf.price}
          testimonial={acf.testimonial}
          weblink={acf.weblink ? acf.weblink : '/'}
        />
      )
    }
    let color = propertyData.website_status !== 'sold' && propertyData.status !== 'LEASED'

    let props = Object.assign({}, el);

    if (propertyData.address_undisclosed) {
      props.title = propertyMeta.suburb
    }

    return (
      <PropertyCard {...props} color={color} key={el.id} />
    )
  }

  let cards = list.slice(0, length).map(_renderCard)
  // Fade component fails if going from multiple to single
  // So we add another element so it's always mulitple
  if (cards.length === 1) cards.push(<div key='f' />)

  return (
    <>
      <SearchForm list={searchProperties} set={_formCallback} {...props} />
      <section className='property-blocks' data-color={dark ? '#4B4B4D' : '#ffffff'}>
        <div className='property-blocks__inner'>
          <div className='property-blocks__options'>
            <div className="property-blocks__option">
              <p className="title">Order.</p>
              <div className='select' onMouseOver={() => orderRef.current.focus()}>
                <Select
                  components={{ DropdownIndicator }}
                  options={orderOptions}
                  styles={colourStyles}
                  onChange={_orderCallback}
                  openMenuOnFocus={true}
                  isSearchable={false}
                  id="order"
                  name="order"
                  placeholder="Newest"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  ref={orderRef}
                />
              </div>
            </div>
          </div>
          <div className={`property-blocks__list ${columns}`}>
            {cards.length > 0 &&
              <Fade bottom distance='50px' duration={500}>
                {cards}
              </Fade>
            }
          </div>
          {list.length > cards.length && pagePropertyBlocks !== "WFLanding" &&
            <div className='property-blocks__loading' ref={loadRef}>
              <svg width='23px' height='23px' viewBox='0 0 23 23' version='1.1' xmlns='http://www.w3.org/2000/svg'>
                <g stroke='none' strokeWidth='1' fill={dark ? '#ffffff' : '#D8D8D9'} fillRule='evenodd'>
                  <polygon points='6.28167018 6.14475993 13.1732595 6.14475993 13.1732595 7.50007435 7.72928201 7.50007435 7.72928201 10.6784242 13.1829495 10.6784242 13.1829495 12.0337387 7.72928201 12.0337387 7.72928201 15.4533439 13.2262548 15.4533439 13.2262548 16.8086311 6.28167018 16.8086311'></polygon>
                  <polygon points='16.6987077 6.14475993 9.80711834 6.14475993 9.80711834 7.50007435 15.2510686 7.50007435 15.2510686 10.6784242 9.7974284 10.6784242 9.7974284 12.0337387 15.2510686 12.0337387 15.2510686 15.4533439 9.75409586 15.4533439 9.75409586 16.8086311 16.6987077 16.8086311'></polygon>
                  <path d='M11.4902284,22.9533203 C5.15453136,22.9533203 9.80548975e-15,17.8048546 9.80548975e-15,11.4766466 C9.80548975e-15,5.14846572 5.15453136,-1.30739863e-14 11.4902284,-1.30739863e-14 C17.8259254,-1.30739863e-14 22.980484,5.14846572 22.980484,11.4766466 C22.980484,17.8048546 17.8259254,22.9533203 11.4902284,22.9533203 M11.4902284,0.940526005 C5.67378698,0.940526005 0.941639053,5.66708038 0.941639053,11.4766466 C0.941639053,17.28624 5.67378698,22.0127943 11.4902284,22.0127943 C17.3066698,22.0127943 22.0388178,17.28624 22.0388178,11.4766466 C22.0388178,5.66708038 17.3066698,0.940526005 11.4902284,0.940526005'></path>
                </g>
              </svg>
            </div>
          }
          {
            list.length > cards.length && pagePropertyBlocks === "WFLanding" &&
            <div className="property-blocks__load-more" onClick={() => setLength(length + 12)} >
              Load More
            </div>
          }
          {list.length === 0 &&
            <div className='property-blocks__warning'>No properties found, please refine your search</div>
          }

        </div>
      </section>
    </>
  )
}

export default PropertyBlocks